<template>
  <section class="tp-purchases_wrap">
    <material-form
      ref="materialForm"
      @created="materialCreationHandler"
      @canceled="resetVisibility"/>

    <supplier-form
      ref="supplierForm"
      @created="supplierCreationHandler"
      @canceled="resetVisibility"/>

    <PurchasesCreateFormReact
      v-if="mode === 'create'"
      :open="isOpen"
      :defaultValues="formValues"
      :supplierOption="selectedSupplier"
      :materialOption="selectedMaterial"
      :disableMaterial="disableMaterial"
      :minQuantity="minQuantity"
      :onClose="handleClose"
      :onCancel="handleCancel"
      :onCreated="handleCreated"
      :onRequestNewSupplier="handleRequestNewSupplier"
      :onRequestNewMaterial="handleRequestNewMaterial"
    />
    <PurchasesEditFormReact
      v-else
      :open="isOpen"
      :defaultValues="formValues"
      :supplierOption="selectedSupplier"
      :materialOption="selectedMaterial"
      :disableMaterial="disableMaterial"
      :minQuantity="minQuantity"
      :onClose="handleClose"
      :onCancel="handleCancel"
      :onUpdated="handleUpdated"
      :onRequestNewSupplier="handleRequestNewSupplier"
      :onRequestNewMaterial="handleRequestNewMaterial"
    />
  </section>
</template>

<script>
import MaterialFormCmp from "../material/MaterialForm.vue";
import SupplierFormCmp from "../suppliers/SuppliersForm.vue";
import ReactInVue from "@/components/logic/wrappers/ReactInVue";
import { PurchasesCreateFormController } from "@/modules/purchases/components/PurchasesCreateFormController";
import { PurchasesEditFormController } from "@/modules/purchases/components/PurchasesEditFormController";

const PurchasesCreateFormReact = ReactInVue(PurchasesCreateFormController);
const PurchasesEditFormReact = ReactInVue(PurchasesEditFormController);

const buildMaterialLabel = (material = {}) => {
  const parts = [material.brand, material.model, material.description]
    .filter(Boolean)
    .map(String);
  return parts.join(", ");
};

const DEFAULT_FORM_VALUES = {
  id: "",
  supplier: "",
  material: "",
  origin: "",
  price: 1,
  quantity: 1,
  observation: "",
  link: "",
  request: "",
};

const normalizeSupplier = (supplier = null) => {
  if (!supplier) {
    return null;
  }

  return {
    id: supplier.id || supplier._id || "",
    name: supplier.name || "",
  };
};

const normalizeMaterial = (material = null) => {
  if (!material) {
    return null;
  }

  return {
    id: material.id || material._id || "",
    label: buildMaterialLabel(material),
  };
};

export default {
  name: "PurchasesForm",
  components: {
    MaterialForm: MaterialFormCmp,
    SupplierForm: SupplierFormCmp,
    PurchasesCreateFormReact,
    PurchasesEditFormReact,
  },
  data() {
    return {
      isOpen: false,
      mode: "create",
      formValues: { ...DEFAULT_FORM_VALUES },
      selectedSupplier: null,
      selectedMaterial: null,
      disableMaterial: false,
      minQuantity: 1,
      pendingRequest: null,
      temporarilyHidden: false,
    };
  },
  methods: {
    show(val = true) {
      if (val) {
        this.isOpen = true;
        this.temporarilyHidden = false;
        return;
      }

      this.handleClose();
    },
    reset() {
      this.formValues = { ...DEFAULT_FORM_VALUES };
      this.selectedSupplier = null;
      this.selectedMaterial = null;
      this.disableMaterial = false;
      this.minQuantity = 1;
      this.pendingRequest = null;
      this.mode = "create";
    },
    handleClose() {
      this.isOpen = false;
      this.temporarilyHidden = false;
      this.reset();
    },
    handleCancel() {
      this.$emit("canceled");
    },
    handleCreated(purchase) {
      this.$emit("created", purchase);
    },
    handleUpdated(purchase) {
      this.$emit("updated", purchase);
    },
    handleRequestNewSupplier() {
      this.temporarilyHidden = true;
      this.isOpen = false;
      this.$refs.supplierForm.show();
    },
    handleRequestNewMaterial() {
      if (this.disableMaterial) {
        return;
      }

      const panel = this.$refs.materialForm;
      if (panel && typeof panel.enableSimpleMode === "function") {
        panel.enableSimpleMode();
      }

      this.temporarilyHidden = true;
      this.isOpen = false;
      this.$refs.materialForm.show();
    },
    resetVisibility() {
      if (!this.temporarilyHidden) {
        return;
      }

      this.temporarilyHidden = false;
      this.isOpen = true;
    },
    setMaterial(material, quantity = 1, request = null) {
      const normalized = normalizeMaterial(material);
      if (!normalized) {
        return;
      }

      this.selectedMaterial = normalized;
      this.formValues = {
        ...this.formValues,
        material: normalized.id,
        quantity: quantity || 1,
        request: request || "",
      };

      this.disableMaterial = true;
      this.minQuantity = quantity || 1;
      this.isOpen = true;
      this.temporarilyHidden = false;
    },
    materialCreationHandler(material) {
      const normalized = normalizeMaterial(material);
      if (!normalized) {
        return;
      }

      this.selectedMaterial = normalized;
      this.formValues = {
        ...this.formValues,
        material: normalized.id,
      };

      this.resetVisibility();
    },
    supplierCreationHandler(supplier) {
      const normalized = normalizeSupplier(supplier);
      if (normalized) {
        this.selectedSupplier = normalized;
        this.formValues = {
          ...this.formValues,
          supplier: normalized.id,
        };
      }

      this.resetVisibility();
    },
    setPurchase(purchase) {
      if (!purchase) {
        return;
      }

      this.mode = "edit";
      this.disableMaterial = false;
      this.minQuantity = 1;
      this.temporarilyHidden = false;

      const normalizedPurchase = {
        id: purchase.id || "",
        supplier: purchase.supplier?.id || purchase.supplier || "",
        material: purchase.material?.id || purchase.material || "",
        origin: purchase.origin || "",
        price: Number(purchase.price ?? 1),
        quantity: Number(purchase.quantity ?? 1),
        observation: purchase.observation || "",
        link: purchase.link || "",
        request: purchase.request || "",
      };

      this.formValues = { ...DEFAULT_FORM_VALUES, ...normalizedPurchase };

      const materialId = normalizedPurchase.material;
      const supplierId = normalizedPurchase.supplier;

      if (purchase.material) {
        this.selectedMaterial = normalizeMaterial({
          ...(purchase.material || {}),
          id: materialId,
        });
      } else {
        this.selectedMaterial = null;
      }

      if (purchase.supplier) {
        this.selectedSupplier = normalizeSupplier({
          ...(purchase.supplier || {}),
          id: supplierId,
        });
      } else {
        this.selectedSupplier = null;
      }

      if (materialId) {
        this.$stellar
          .action("getMaterial", { id: materialId })
          .then(({ material }) => {
            this.selectedMaterial = normalizeMaterial({ ...material, id: materialId });
          })
          .catch(() => {
            this.selectedMaterial = null;
          });
      } else {
        this.selectedMaterial = null;
      }

      if (supplierId) {
        this.$stellar
          .action("getSupplier", { id: supplierId })
          .then(({ supplier }) => {
            this.selectedSupplier = normalizeSupplier({ ...supplier, id: supplierId });
          })
          .catch(() => {
            this.selectedSupplier = null;
          });
      } else {
        this.selectedSupplier = null;
      }
    },
    showMaterialForm() {
      this.handleRequestNewMaterial();
    },
    showSupplierForm() {
      this.handleRequestNewSupplier();
    },
    searchForMaterials() {
      // no-op retained for backward compatibility
    },
    searchForSuppliers() {
      // no-op retained for backward compatibility
    },
    savePurchase() {
      // handled inside React controller
    },
    saveChangesPurchase() {},
    addPurchase() {},
    resetForm() {
      this.handleClose();
    },
  },
};
</script>

<style scoped>
.tp-Purchases__fullField {
  display: block;
  width: 100%;
}

.tp-Purchases__newButton {
  margin-top: 60px !important;
}
</style>
